import { useState, useEffect, useMemo } from "react";
import PageLayout from "../../../Layouts/PageLayout/PageLayout";
import TabsNavigation from "../../../components/common/TabsNavigation/TabsNavigation";
import Button from "../../../components/common/Button/Button";
import BarChartGraph from "../../../components/common/Charts/BarChartGraph";
import Table from "../../../components/common/Table/Table";
import { FiDownload, FiTrendingDown } from "react-icons/fi";
import { MdOutlineFileDownload } from "react-icons/md";
import { getApiInstance } from "../../../utils/api";
import { useZone } from "../../../context/ZoneContext";
import { ZONE_LABEL_MAP } from "../../../constants/zones";
import {
    useExcelExport,
    codsFilaDe,
    zonaExportFromFilterState,
} from "../useExcelExport";
import { useMoneda } from "../../../context/MonedaContext";
import {
    formateadorMoneda,
    hayImportesConvertidos,
} from "../../../utils/moneda";
import Modal from "../../../components/common/Modal/Modal";
import { FilterBar } from "../../../components/filtersNew/filter-barNew";
import ExcluirRaeeZeroWasteToggle, { filtrarProyectosExcluidos } from "../../../components/common/Inputs/ExcluirRaeeZeroWasteToggle/ExcluirRaeeZeroWasteToggle";
import { Field } from "../../../components/ui-kitNew/controlsNew";
import { Select } from "../../../components/ui-kitNew/selectNew";
import {
    createFilterState,
    applyFilterState,
} from "../../../lib/reportNew/filter-stateNew";
import "../cantidadesGeneradas/CantidadesGeneradas.scss";
import "./Costes.scss";

const MiniGauge = ({ percentage = 0, size = 28 }) => {
    const pct = Math.max(0, Math.min(100, Number(percentage) || 0));

    const cx = size / 2;
    const cy = size / 2;
    const r = size / 2 - 2;

    const point = (angle) => {
        const rad = (angle * Math.PI) / 180;
        return {
            x: cx + r * Math.cos(rad),
            y: cy - r * Math.sin(rad),
        };
    };

    const startAngle = 180;
    const endAngle = 180 - pct * 1.8;

    const start = point(startAngle);
    const end = point(endAngle);
    const largeArc = pct > 50 ? 1 : 0;
    const sweep = 1;

    const arcPath = `M ${start.x} ${start.y} A ${r} ${r} 0 ${largeArc} ${sweep} ${end.x} ${end.y}`;

    return (
        <svg
            width={size}
            height={size}
            viewBox={`0 0 ${size} ${size}`}
            className="costes-mini-gauge"
            aria-label={`${pct.toFixed(2)}%`}
        >
            <path
                d={`M ${start.x} ${start.y} A ${r} ${r} 0 1 ${sweep} ${point(0).x} ${point(0).y}`}
                fill="none"
                stroke="#e5e7eb"
                strokeWidth={4}
                strokeLinecap="round"
            />
            {pct > 0 && (
                <path
                    d={arcPath}
                    fill="none"
                    stroke="#0b6775"
                    strokeWidth={4}
                    strokeLinecap="round"
                />
            )}
            <circle
                cx={end.x}
                cy={end.y}
                r={4}
                fill="#0b6775"
                className="costes-mini-gauge__dot"
            />
        </svg>
    );
};

const crearColumnasCostes = (categoriaLabel, formatoMoneda) => [
    {
        key: "categoria",
        label: categoriaLabel,
        visible: true,
        sortable: true,
        render: (row) => (
            <a
                href="#"
                className="link-style"
                onClick={(e) => {
                    e.preventDefault();
                    // TODO: definir la navegación al pulsar la categoría
                }}
            >
                {row.categoria}
            </a>
        ),
    },
    {
        key: "costeReal",
        label: "Coste Real",
        visible: true,
        sortable: true,
        type: "number",
        render: (row) => formatoMoneda.format(row.costeReal),
    },
    {
        key: "costePrevisto",
        label: "Coste Previsto",
        visible: true,
        sortable: true,
        type: "number",
        render: (row) => formatoMoneda.format(row.costePrevisto),
    },
    {
        key: "diferencia",
        label: "Diferencia",
        visible: true,
        sortable: true,
        type: "number",
        render: (row) => (
            <span className="costes-diferencia">
                {formatoMoneda.format(row.diferencia)}
                <FiTrendingDown
                    className="costes-diferencia__icon"
                    aria-label="descendente"
                />
            </span>
        ),
    },
    {
        key: "porcentaje",
        label: "Porcentaje",
        visible: true,
        sortable: true,
        type: "number",
        render: (row) => (
            <span className="costes-porcentaje">
                <MiniGauge percentage={row.porcentaje} />
                <span className="costes-porcentaje__value">
                    {row.porcentaje.toFixed(2)}%
                </span>
            </span>
        ),
    },
];

export default function Costes() {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState("grafica");

    const [informeCostes, setInformeCostes] = useState([]);

    // Estado para los filtros
    const [selectedAgrupacion, setSelectedAgrupacion] = useState("cadena");

    const { selectedZone } = useZone();
    const { moneda } = useMoneda();

    const filterFields = [
        {
            key: "rangoFechas",
            label: "Rango de fechas",
            type: "dateRange",
            rangeEndField: "fechaTratamiento",
        },
        {
            key: "codUbicacion",
            label: "Ubicación",
            type: "enum",
            labelMap: ["", "Calle", "Centro comercial"],
        },
        { key: "intermediario", label: "Intermediario", type: "enum" },
        { key: "provincia", label: "Provincia", type: "enum" },
        { key: "codObra", label: "Centro de producción", type: "enum" },
        { key: "cadena", label: "Cadena", type: "enum" },
        { key: "tipoObra", label: "Tipo de obra", type: "enum" },
        { key: "pais", label: "País", type: "enum" },
        {
            key: "codZona",
            label: "Zona",
            type: "enum",
            labelMap: ZONE_LABEL_MAP,
        },
    ];

    const defaultQuickFilters = [
        "rangoFechas",
        "codUbicacion",
        "intermediario",
        "provincia",
        "codZona",
    ];

    const [filterState, setFilterState] = useState(() =>
        createFilterState(defaultQuickFilters)
    );

    const { isModalExcelOpen, openModal, closeModal, handleExportToExcel, handleExportInformeCompleto, exportando } =
        useExcelExport({
            onError: setError,
            resumenUrl: "api/Informe/CreateReportExcelCostesResumen",
        });

           /* Se vuelve a pedir el informe al cambiar de moneda: la conversión la hace el
       backend, para que la pantalla y el Excel no puedan discrepar. */
    const zonaExport = useMemo(
        () => zonaExportFromFilterState(filterState),
        [filterState]
    );

    useEffect(() => {
        if (!moneda) return;

        const fetchInformeCostes = async () => {
            try {
                setLoading(true);
                const api = getApiInstance();
                const response = await api.get(
                    `/api/Informe/GetCostes?moneda=${moneda}`
                );
                const data = Array.isArray(response.data)
                    ? response.data
                    : [];
                setInformeCostes(data);
            } catch (err) {
                console.error(err);
            } finally {
                setLoading(false);
            }
        };
        fetchInformeCostes();
    }, [moneda]);

    const agrupaciones = [
        { value: "cadena", label: "Por cadena" },
        { value: "tipoObra", label: "Por tipo de obra" },
        { value: "pais", label: "Por país" },
    ];

    const categoriaLabels = {
        cadena: "Cadena",
        tipoObra: "Tipo de Obra",
        pais: "País",
    };
    const categoriaLabel = categoriaLabels[selectedAgrupacion] ?? "Categoría";

    const [excluirRaeeZeroWaste, setExcluirRaeeZeroWaste] = useState(false);

    const datosFiltrados = useMemo(
        () =>
            filtrarProyectosExcluidos(
                applyFilterState(informeCostes, filterState, filterFields),
                excluirRaeeZeroWaste
            ),
        [informeCostes, filterState, filterFields, excluirRaeeZeroWaste]
    );

    const { datosBarChart, datosListado, totalCosteReal, totalCostePrevisto } =
        useMemo(() => {
            const claveAgrupacion =
                selectedAgrupacion === "pais"
                    ? "pais"
                    : selectedAgrupacion === "tipoObra"
                    ? "tipoObra"
                    : "cadena";

            const agregados = datosFiltrados.reduce((acc, item) => {
                const clave = item[claveAgrupacion] ?? "Sin clasificar";
                if (!acc[clave]) {
                    acc[clave] = { costeReal: 0, costePrevisto: 0 };
                }
                acc[clave].costeReal += item.costeReal;
                acc[clave].costePrevisto += item.costePrevisto;
                return acc;
            }, {});

            const totalReal = datosFiltrados.reduce(
                (sum, item) => sum + item.costeReal,
                0
            );
            const totalPrevisto = datosFiltrados.reduce(
                (sum, item) => sum + item.costePrevisto,
                0
            );

            const datosProcesados = Object.keys(agregados).map((clave) => {
                const costeReal = agregados[clave].costeReal;
                const costePrevisto = agregados[clave].costePrevisto;
                const diferencia = costeReal - costePrevisto;
                const porcentaje =
                    totalReal > 0 ? (costeReal / totalReal) * 100 : 0;
                return {
                    categoria: clave,
                    costeReal,
                    costePrevisto,
                    diferencia,
                    porcentaje,
                };
            });

            return {
                datosBarChart: datosProcesados.map((item) => ({
                    name: item.categoria,
                    costeReal: item.costeReal,
                    costePrevisto: item.costePrevisto,
                })),
                datosListado: datosProcesados,
                totalCosteReal: totalReal,
                totalCostePrevisto: totalPrevisto,
            };
        }, [datosFiltrados, selectedAgrupacion]);

    /* Los importes llegan ya convertidos a la moneda del selector; aquí solo se
       formatea. */
    const importesConvertidos = useMemo(
        () => hayImportesConvertidos(datosFiltrados, moneda),
        [datosFiltrados, moneda]
    );

    const formatoMoneda = useMemo(() => formateadorMoneda(moneda), [moneda]);
    const formatCurrency = (value) => formatoMoneda.format(value);

    const columnasCostes = useMemo(
        () => crearColumnasCostes(categoriaLabel, formatoMoneda),
        [categoriaLabel, formatoMoneda]
    );

    const tabs = [
        { id: "grafica", label: "Gráfica" },
        { id: "listado", label: "Listado" },
    ];

    const renderFilters = () => (
        <div className="filters-section-common">
            <FilterBar
                rows={informeCostes}
                state={filterState}
                onChange={setFilterState}
                resultCount={datosFiltrados.length}
                totalCount={informeCostes.length}
                fields={filterFields}
                headerExtra={
                    <div className="flex items-end gap-8">
                        <Field label="Agrupar por">
                            <Select
                                options={agrupaciones}
                                value={selectedAgrupacion}
                                onChange={setSelectedAgrupacion}
                                placeholder="Agrupar por"
                                triggerClassName="!h-[40px] px-3 !text-[15px]"
                            />
                        </Field>
                        <ExcluirRaeeZeroWasteToggle
                            activo={excluirRaeeZeroWaste}
                            onChange={setExcluirRaeeZeroWaste}
                        />
                    </div>
                }
                excludeFields={["codTipoObra", "codCadena","codIntermediario", "ubicacion", "codProvincia", "codPais", "codFila", "codProyecto"]}
            />
        </div>
    );

    /* Entre pesos y euros hay un factor 20: sin decir con qué cambio se ha
       convertido, un mismo informe en dos monedas parece dos informes distintos. */
    const renderNotaConversion = () =>
        importesConvertidos ? (
            <p className="costes-nota-conversion">
                Importes en {moneda}. Las obras grabadas en otra moneda se
                muestran convertidas con el cambio medio del semestre de su fecha
                de tratamiento.
            </p>
        ) : null;

    const renderGraficaContent = () => (
        <div className="tab-content-with-filters">
            {renderFilters()}
            {renderNotaConversion()}

            <div className="cantidades-generadas-content">
                <div className="graficas-card">
                    <div className="charts-section">
                        <div className="chart-container bar-chart-container">
                            <div className="chart-header">
                                <h3>Coste Total</h3>
                                <div className="total-badge">
                                    <span className="total-badge__value">
                                        {formatCurrency(totalCosteReal)}
                                    </span>
                                </div>
                            </div>
                            <div className="chart-wrapper">
                                <BarChartGraph
                                    data={{ all: datosBarChart }}
                                    activeTab="all"
                                    yTicks={[
                                        0,
                                        5000000,
                                        10000000,
                                    ]}
                                    yFormatter={(v) =>
                                        v >= 1000000
                                            ? `${v / 1000000}M`
                                            : v
                                    }
                                    colors={["#0b6775", "#5599a3"]}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    const renderListadoContent = () => (
        <div className="tab-content-with-filters">
            {renderFilters()}
            {renderNotaConversion()}

            <div className="listado-content">
                <Table
                    columns={columnasCostes}
                    dataTable={datosListado}
                    keyProp="categoria"
                    sortProp="categoria"
                    searchTerms={[
                        "categoria",
                        "costeReal",
                        "costePrevisto",
                        "diferencia",
                        "porcentaje",
                    ]}
                    showCheckboxes={false}
                    noResultsMessage="No se han encontrado costes con los filtros aplicados."
                    elementName={categoriaLabel.toLowerCase()}
                    loading={loading}
                    rowsPerPage={10}
                />
            </div>
        </div>
    );

    return (
        <div className="costes-page">
            <PageLayout
                title="Informe de Costes"
                subtitle="Análisis detallado de costes reales vs. previstos por obra"
                actions={[
                    <Button
                        key="export"
                        componentType="button"
                        variant="filled"
                        startIcon={<FiDownload />}
                        onClick={openModal}
                    >
                        Exportar a excel
                    </Button>,
                ]}
            >
                <TabsNavigation
                    tabs={tabs}
                    activeTab={activeTab}
                    onTabChange={setActiveTab}
                />

                {error && <div className="error-message">{error}</div>}

                <Modal
                    isOpen={isModalExcelOpen}
                    onClose={closeModal}
                    title="Exportar informe a Excel"
                    actions={[
                        <Button
                            key="export-resumen"
                            className="export-excel__btn export-excel__btn--primary"
                            onClick={() =>
                                handleExportToExcel("resumen", {
                                    ...zonaExport,
                                    agrupacion: selectedAgrupacion,
                                    codsFila: codsFilaDe(datosFiltrados),
                                })
                            }
                            startIcon={<MdOutlineFileDownload />}
                            disabled={exportando}
                        >
                            {exportando === "resumen"
                                ? "Generando..."
                                : "Informe Costes"}
                        </Button>,
                        <Button
                            key="export-completo"
                            className="export-excel__btn export-excel__btn--secondary"
                            onClick={() => handleExportInformeCompleto({ filterState, excluirRaeeZeroWaste })}
                            startIcon={<MdOutlineFileDownload />}
                            disabled={exportando}
                        >
                            {exportando === "completo"
                                ? "Generando..."
                                : "Informe Completo"}
                        </Button>,
                    ]}
                >
                    <div className="export-excel__actions"></div>
                </Modal>

                {loading ? (
                    activeTab === "listado" ? (
                        <div
                            className="informe-table-skeleton"
                            aria-hidden="true"
                        >
                            <div className="informe-table-skeleton__row" />
                            <div className="informe-table-skeleton__row" />
                            <div className="informe-table-skeleton__row" />
                            <div className="informe-table-skeleton__row" />
                        </div>
                    ) : (
                        <div
                            className="informe-chart-skeleton informe-chart-skeleton--bar"
                            aria-hidden="true"
                        >
                            <div className="informe-chart-skeleton__bars">
                                {[80, 55, 70, 40, 62, 30, 48].map(
                                    (h, i) => (
                                        <div
                                            key={i}
                                            className="informe-chart-skeleton__bar"
                                            style={{ height: `${h}%` }}
                                        />
                                    )
                                )}
                            </div>
                        </div>
                    )
                ) : activeTab === "grafica" ? (
                    renderGraficaContent()
                ) : (
                    renderListadoContent()
                )}
            </PageLayout>
        </div>
    );
}
